import { getCycleNodesInGraph, getCyclesInGraph, makeGraphFromEdges } from "@glideapps/graphs";
import { DefaultMap, definedMap } from "@glideapps/ts-necessities";
import deepEqual from "deep-equal";
import sortBy from "lodash/sortBy";
import uniqBy from "lodash/uniqBy";
import { deserializeEvaluable } from "../common/components/deserialization";
import type { SourceMetadata } from "../common/components/SerializedApp";
import { isNativeTable } from "../common/components/SerializedApp";
import type { Evaluable, UserProfileTableInfo } from "../common/components/types";
import type { TableName } from "../common/database-strings";
import { logError } from "../common/debug-print";
import type {
    ColumnType,
    Formula,
    TableAndColumn,
    TableColumn,
    TableGlideType,
    TypeSchema,
} from "../common/description";
import {
    areTableNamesEqual,
    canBeRowIDColumn,
    decomposeRelationType,
    FormulaKind,
    getEmailOwnersColumnNames,
    getTableColumn,
    getTableName,
    isComputedColumn,
    isDataSourceColumn,
    isPrimitiveType,
    makeTypeSchema,
} from "../common/description";
import type {
    ApplyColumnFormatFormula,
    ArrayContainsFormula,
    AssignVariablesFormula,
    ConstructURLFormula,
    FilterRowsFormula,
    FindRowFormula,
    FormatDateTimeFormula,
    FormatNumberFixedFormula,
    GenerateImageFormula,
    GeocodeAddressFormula,
    GetColumnFormula,
    GetNthFormula,
    GetTableRowsFormula,
    GetVariableFormula,
    IfThenElseFormula,
    IsInRangeFormula,
    JoinStringsFormula,
    LeftRightFormula,
    MapRowsFormula,
    RandomPickFormula,
    ReduceFormula,
    ReduceToMemberByFormula,
    SplitStringFormula,
    StartOrEndOfDayFormula,
    TextTemplateFormula,
    UnaryMathFormula,
    UserAPIFetchFormula,
    WithFormula,
    WithUserEnteredTextFormula,
    YesCodeFormula,
} from "../common/formula";
import { areSetsEqual, isArray, mapFilterUndefined, replaceArrayItem, truthify } from "../support";
import type { SchemaInspector } from "./components";
import { SimpleFetchingBuilder, SimpleFetchingEvaluatingBuilder } from "./components";
import { makeSimpleSchemaInspector } from "./components/simple-ccc";
import { areColumnTypesEqualExceptPrimitives, compareSchemas } from "./description-utils";
import { compileFormula } from "./formulas";
import { makeUniqueIDForColumnFormula } from "./formulas/compiler";
import { containingScreenContextName } from "./formulas/formula-specifications";

export function makeTypeForComputedColumn(
    schema: TypeSchema,
    userProfileTableInfo: UserProfileTableInfo | undefined,
    sourceTable: TableGlideType,
    formula: Formula,
    forBuilder: boolean
): ColumnType | readonly string[] {
    const compileResult = compileFormula(
        new SimpleFetchingBuilder(schema, userProfileTableInfo, forBuilder),
        sourceTable,
        formula,
        false,
        false,
        "makeTypeForComputedColumn shouldn't need a unique ID"
    );
    if (!compileResult.success) return compileResult.errors;
    if (compileResult.type === undefined) {
        return { kind: "string" };
    } else {
        return compileResult.type;
    }
}

// This has to return `schema` if there are no changes, i.e. if everything
// was correct.
export function sanitizeSchemaAfterChanges(
    schema: TypeSchema,
    userProfileTableInfo: UserProfileTableInfo | undefined
): TypeSchema {
    // We apply changes until nothing changes anymore.
    // We do this because we might have dependencies between computed columns.
    for (;;) {
        let didChange = false;

        // eslint-disable-next-line no-loop-func
        const tables = schema.tables.map(table => {
            const columns = mapFilterUndefined(table.columns, column => {
                const { formula } = column;
                if (formula === undefined) return column;

                const type = makeTypeForComputedColumn(schema, userProfileTableInfo, table, formula, false);
                if (isArray(type)) {
                    logError(type);
                    // This is where we used to delete columns.  We don't do this
                    // anymore because it's too disruptive.
                    return column;
                }

                if (deepEqual(column.type, type, { strict: true })) {
                    return column;
                }

                didChange = true;
                return { ...column, type };
            });
            const uniqueColumns = uniqBy(columns, c => c.name);
            if (columns.length !== uniqueColumns.length) {
                didChange = true;
            }

            // Some old apps have duplicate column names
            let result = { ...table, columns: uniqueColumns };

            const { rowIDColumn } = result;
            const emailOwnersColumns = getEmailOwnersColumnNames(result);
            const existingEmailOwnersColumns = emailOwnersColumns.filter(c => getTableColumn(result, c) !== undefined);
            if (!areSetsEqual(emailOwnersColumns, existingEmailOwnersColumns)) {
                result = { ...result, emailOwnersColumn: existingEmailOwnersColumns };
                didChange = true;
            }
            if (rowIDColumn !== undefined && getTableColumn(result, rowIDColumn) === undefined) {
                result = { ...result, rowIDColumn: undefined };
                didChange = true;
            }

            return result;
        });

        if (didChange) {
            schema = makeTypeSchema(tables);
            continue;
        }

        return schema;
    }
}

export function getColumnsUsedInFormula(
    schema: SchemaInspector,
    contextTableName: TableName | undefined,
    containingScreenTableName: TableName | undefined,
    rootFormula: Formula
): ReadonlyMap<TableGlideType, ReadonlySet<string>> {
    const columnsUsed = new DefaultMap<TableGlideType, Set<string>>(() => new Set());

    const contextTable = schema.findTable(contextTableName);
    const containingScreenTable = schema.findTable(containingScreenTableName);

    function addUsed(
        formula: Formula,
        ctxs: ReadonlyMap<string, TableGlideType>,
        vars: ReadonlyMap<string, TableGlideType>
    ): TableGlideType | undefined {
        function addContext(name: string, table: TableGlideType | undefined) {
            if (table === undefined) return ctxs;
            return new Map([...ctxs, [name, table]]);
        }

        switch (formula.kind) {
            case FormulaKind.FilterRows:
            case FormulaKind.FindRow: {
                const f = formula as FilterRowsFormula | FindRowFormula;
                const c = addUsed(f.rows, ctxs, vars);
                addUsed(f.predicate, addContext(f.predicateContextName, c), vars);
                return c;
            }
            case FormulaKind.MapRows: {
                const f = formula as MapRowsFormula;
                const c = addUsed(f.rows, ctxs, vars);
                addUsed(f.function, addContext(f.functionContextName, c), vars);
                break;
            }
            case FormulaKind.Reduce: {
                const f = formula as ReduceFormula;
                const c = addUsed(f.rows, ctxs, vars);
                addUsed(f.value, addContext(f.valueContextName, c), vars);
                break;
            }
            case FormulaKind.ReduceToMemberBy: {
                const f = formula as ReduceToMemberByFormula;
                const c = addUsed(f.rows, ctxs, vars);
                const nctxs = addContext(f.valueContextName, c);
                addUsed(f.key, nctxs, vars);
                return addUsed(f.value, nctxs, vars);
            }
            case FormulaKind.JoinStrings: {
                const f = formula as JoinStringsFormula;
                const c = addUsed(f.rows, ctxs, vars);
                addUsed(f.value, addContext(f.valueContextName, c), vars);
                addUsed(f.separator, ctxs, vars);
                break;
            }
            case FormulaKind.SplitString: {
                const f = formula as SplitStringFormula;
                addUsed(f.string, ctxs, vars);
                addUsed(f.separator, ctxs, vars);
                break;
            }
            case FormulaKind.IfThenElse: {
                const f = formula as IfThenElseFormula;
                addUsed(f.condition, ctxs, vars);
                const u = addUsed(f.consequent, ctxs, vars);
                const v = definedMap(f.alternative, a => addUsed(a, ctxs, vars));
                if (u === v) {
                    return u;
                }
                break;
            }
            case FormulaKind.CompareValues:
            case FormulaKind.ArraysOverlap:
            case FormulaKind.BinaryMath:
            case FormulaKind.And:
            case FormulaKind.Or:
            case FormulaKind.GeoDistance: {
                const f = formula as LeftRightFormula;
                addUsed(f.left, ctxs, vars);
                addUsed(f.right, ctxs, vars);
                break;
            }
            case FormulaKind.GetColumn: {
                const f = formula as GetColumnFormula;
                const u = f.contextName === undefined ? contextTable : ctxs.get(f.contextName);
                if (u !== undefined) {
                    const c = getTableColumn(u, f.column);
                    if (c !== undefined) {
                        columnsUsed.get(u).add(f.column);
                        const tableRef = decomposeRelationType(c.type)?.tableRef;
                        if (tableRef !== undefined) {
                            return schema.findTable(tableRef);
                        }
                    }
                }
                break;
            }
            case FormulaKind.TextTemplate: {
                const f = formula as TextTemplateFormula;
                addUsed(f.template, ctxs, vars);
                for (const { pattern, replacement } of f.replacements) {
                    addUsed(pattern, ctxs, vars);
                    addUsed(replacement, ctxs, vars);
                }
                break;
            }
            case FormulaKind.ArrayContains: {
                const f = formula as ArrayContainsFormula;
                addUsed(f.array, ctxs, vars);
                addUsed(f.item, ctxs, vars);
                break;
            }
            case FormulaKind.CheckValue:
            case FormulaKind.Not:
            case FormulaKind.ConvertToType:
            case FormulaKind.ApplyColumnFormat:
                addUsed((formula as ApplyColumnFormatFormula).value, ctxs, vars);
                break;
            case FormulaKind.StartOfDay:
            case FormulaKind.EndOfDay:
                addUsed((formula as StartOrEndOfDayFormula).dateTime, ctxs, vars);
                break;
            case FormulaKind.With: {
                const f = formula as WithFormula;
                const u = addUsed(f.context, ctxs, vars);
                return addUsed(f.value, addContext(f.contextName, u), vars);
            }
            case FormulaKind.GetNth:
            case FormulaKind.GetNthLast: {
                const f = formula as GetNthFormula;
                const u = addUsed(f.array, ctxs, vars);
                addUsed(f.index, ctxs, vars);
                return u;
            }
            case FormulaKind.RandomPick:
                return addUsed((formula as RandomPickFormula).array, ctxs, vars);
            case FormulaKind.IsInRange: {
                const f = formula as IsInRangeFormula;
                addUsed(f.value, ctxs, vars);
                addUsed(f.start, ctxs, vars);
                addUsed(f.end, ctxs, vars);
                break;
            }
            case FormulaKind.AssignVariables: {
                const f = formula as AssignVariablesFormula;
                const newVars = new Map(vars);
                for (const [name, rhs] of f.assignments) {
                    const u = addUsed(rhs, ctxs, newVars);
                    if (u !== undefined) {
                        newVars.set(name, u);
                    }
                }
                return addUsed(f.body, ctxs, newVars);
            }
            case FormulaKind.Random:
                break;
            case FormulaKind.UnaryMath:
                addUsed((formula as UnaryMathFormula).operand, ctxs, vars);
                break;
            case FormulaKind.Constant:
            case FormulaKind.Empty:
            case FormulaKind.SpecialValue:
            case FormulaKind.CurrentLocation:
            case FormulaKind.GetContext:
                break;
            case FormulaKind.GetVariable:
                return vars.get((formula as GetVariableFormula).name);
            case FormulaKind.GetTableRows:
                return schema.findTable((formula as GetTableRowsFormula).table);
            case FormulaKind.GetUserProfileRow:
                return schema.findTable(schema.userProfileTableInfo?.tableName);
            case FormulaKind.WithUserEnteredText:
                return addUsed((formula as WithUserEnteredTextFormula).formula, ctxs, vars);
            case FormulaKind.FormatNumberFixed: {
                const f = formula as FormatNumberFixedFormula;
                addUsed(f.value, ctxs, vars);
                addUsed(f.decimalsAfterPoint, ctxs, vars);
                break;
            }
            case FormulaKind.FormatDateTime: {
                const f = formula as FormatDateTimeFormula;
                addUsed(f.value, ctxs, vars);
                if (f.dateFormat !== undefined) {
                    addUsed(f.dateFormat, ctxs, vars);
                }
                if (f.timeFormat !== undefined) {
                    addUsed(f.timeFormat, ctxs, vars);
                }
                break;
            }
            case FormulaKind.GeocodeAddress:
                addUsed((formula as GeocodeAddressFormula).address, ctxs, vars);
                break;
            case FormulaKind.GenerateImage: {
                const f = formula as GenerateImageFormula;
                addUsed(f.input, ctxs, vars);
                addUsed(f.imageKind, ctxs, vars);
                break;
            }
            case FormulaKind.UserAPIFetch:
                for (const { name, value } of (formula as UserAPIFetchFormula).params) {
                    addUsed(name, ctxs, vars);
                    addUsed(value, ctxs, vars);
                }
                break;
            case FormulaKind.ConstructURL: {
                const f = formula as ConstructURLFormula;
                for (const { name, value } of f.params) {
                    addUsed(name, ctxs, vars);
                    addUsed(value, ctxs, vars);
                }
                addUsed(f.scheme, ctxs, vars);
                addUsed(f.host, ctxs, vars);
                addUsed(f.path, ctxs, vars);
                break;
            }
            case FormulaKind.YesCode: {
                const f = formula as YesCodeFormula;
                for (const { name, value } of f.params) {
                    addUsed(name, ctxs, vars);
                    addUsed(value, ctxs, vars);
                }
                addUsed(f.url, ctxs, vars);
                break;
            }
            default:
                logError(`Unknown formula kind ${formula.kind}`);
                return undefined;
        }
        return undefined;
    }

    const rootContexts = new Map<string, TableGlideType>();
    if (containingScreenTable !== undefined) {
        rootContexts.set(containingScreenContextName, containingScreenTable);
    }
    addUsed(rootFormula, rootContexts, new Map());
    return columnsUsed;
}

export function getColumnsUsedInColumn(
    schema: SchemaInspector,
    contextTableName: TableName,
    column: TableColumn
): ReadonlyMap<TableGlideType, ReadonlySet<string>> {
    const result = new DefaultMap<TableGlideType, Set<string>>(() => new Set());
    function add(columnsUsed: ReadonlyMap<TableGlideType, ReadonlySet<string>>) {
        for (const [table, columnNames] of columnsUsed) {
            const set = result.get(table);
            for (const cn of columnNames) {
                set.add(cn);
            }
        }
    }

    if (column.formula !== undefined) {
        add(getColumnsUsedInFormula(schema, contextTableName, undefined, column.formula));
    }
    if (column.displayFormula !== undefined) {
        add(getColumnsUsedInFormula(schema, contextTableName, undefined, column.displayFormula));
    }

    return result;
}

function makeColumnAdjacencyForSchema(
    schema: SchemaInspector,
    additionalColumn: { tableName: TableName; columnName: string; formula: Formula } | undefined
): ReadonlyMap<TableAndColumn, Set<TableAndColumn>> {
    const tacForColumn = new Map<TableColumn, TableAndColumn>();
    const columnsUsedByColumns = new DefaultMap<TableAndColumn, Set<TableAndColumn>>(() => new Set());

    function getTableAndColumn(t: TableGlideType, c: TableColumn): TableAndColumn {
        let tac = tacForColumn.get(c);
        if (tac === undefined) {
            tac = { table: t, column: c };
            tacForColumn.set(c, tac);
        }
        return tac;
    }

    for (const table of schema.schema.tables) {
        const tableName = getTableName(table);
        for (const column of table.columns) {
            let formula: Formula;
            if (
                additionalColumn !== undefined &&
                areTableNamesEqual(tableName, additionalColumn.tableName) &&
                column.name === additionalColumn.columnName
            ) {
                formula = additionalColumn.formula;
            } else if (isComputedColumn(column)) {
                formula = column.formula;
            } else {
                continue;
            }

            const used = getColumnsUsedInFormula(schema, tableName, undefined, formula);
            for (const [t, columnNames] of used) {
                for (const columnName of columnNames) {
                    const c = getTableColumn(t, columnName);
                    if (c === undefined) continue;

                    columnsUsedByColumns.get(getTableAndColumn(table, column)).add(getTableAndColumn(t, c));
                }
            }
        }
    }

    return columnsUsedByColumns;
}

export function doesSchemaContainCycles(
    schema: SchemaInspector,
    additionalColumn?: { tableName: TableName; columnName: string; formula: Formula }
): boolean {
    const columnsUsedByColumns = makeColumnAdjacencyForSchema(schema, additionalColumn);
    return getCycleNodesInGraph(makeGraphFromEdges(columnsUsedByColumns)) !== undefined;
}

export type SchemaCycles = readonly (readonly TableAndColumn[])[];

export function getSchemaCycles(
    schema: SchemaInspector,
    additionalColumn?: { tableName: TableName; columnName: string; formula: Formula }
): SchemaCycles | undefined {
    const columnsUsedByColumns = makeColumnAdjacencyForSchema(schema, additionalColumn);
    const graph = makeGraphFromEdges(columnsUsedByColumns);
    const cycleNodes = getCycleNodesInGraph(graph);
    if (cycleNodes === undefined) return undefined;
    return getCyclesInGraph(graph, cycleNodes);
}

export function modifyColumnsInSchema(
    oldSchema: TypeSchema,
    sourceMetadata: readonly SourceMetadata[] | undefined,
    userProfileTableInfo: UserProfileTableInfo | undefined,
    removed: ReadonlyMap<string, readonly string[]>,
    modified: ReadonlyMap<string, readonly TableColumn[]>,
    moved: ReadonlyMap<string, readonly [string, number][]>,
    setProtected: ReadonlyMap<string, readonly [string, boolean][]>,
    rowIDColumns: ReadonlyMap<string, string | undefined>,
    allowDeletingSheetColumns: boolean
): { schema: TypeSchema; errors: readonly string[] } {
    const errors: string[] = [];

    const tables = oldSchema.tables.map(table => {
        const tableName = getTableName(table);
        if (tableName.isSpecial) return table;
        const isNative = sourceMetadata !== undefined && isNativeTable(sourceMetadata, tableName);

        const removedColumns = removed.get(tableName.name) ?? [];
        const modifiedColumns = modified.get(tableName.name) ?? [];
        const setProtectedColumns = setProtected.get(tableName.name) ?? [];

        if (
            removedColumns.length === 0 &&
            modifiedColumns.length === 0 &&
            moved.size === 0 &&
            setProtected.size === 0 &&
            !rowIDColumns.has(tableName.name)
        ) {
            return table;
        }

        const rowIDColumnName = rowIDColumns.get(tableName.name);

        let newColumns: TableColumn[] = [];
        for (let c of table.columns) {
            if (removedColumns.indexOf(c.name) >= 0) {
                if (isNative && c.name === table.rowIDColumn) {
                    c = { ...c, hidden: true };
                } else {
                    if (allowDeletingSheetColumns || !isDataSourceColumn(c, isNative)) continue;

                    errors.push("Trying to delete sheet columns without permission");
                }
            }

            // For native tables
            if (isNative && c.name === rowIDColumnName) {
                // We have some native tables around where the row ID column
                // is protected for some kind, in particular one of the Glide
                // Table template tables.
                // https://github.com/quicktype/glide/issues/11239
                c = { ...c, hidden: false, isProtected: false };
            }

            if (!isNative && isDataSourceColumn(c, isNative)) {
                const setProtectedFlag = setProtectedColumns.find(e => e[0] === c.name)?.[1];
                if (setProtectedFlag !== undefined) {
                    c = { ...c, isProtected: setProtectedFlag };
                }
            }

            newColumns.push(c);
        }

        for (const column of modifiedColumns) {
            if (column.hidden === true) {
                errors.push("Cannot make column hidden");
                continue;
            }
            if (isComputedColumn(column) && column.isUserSpecific === true) {
                errors.push("Computed columns cannot be user-specific");
                continue;
            }
            if (column.displayFormula !== undefined && !isPrimitiveType(column.type)) {
                errors.push("Display formulas not possible on non-primitive columns");
                continue;
            }

            const index = newColumns.findIndex(c => c.name === column.name);
            if (index >= 0) {
                const existingColumn = newColumns[index];

                if (
                    !allowDeletingSheetColumns &&
                    (isDataSourceColumn(column, isNative) !== isDataSourceColumn(existingColumn, isNative) ||
                        truthify(column.isUserSpecific) !== truthify(existingColumn.isUserSpecific))
                ) {
                    errors.push("Cannot replace synthetic column with non-synthetic one, and vice versa");
                    continue;
                }

                newColumns = replaceArrayItem(newColumns, index, column);
            } else {
                if (column.name.length === 0) {
                    errors.push("Cannot create a column with an empty name");
                    continue;
                }
                newColumns.push(column);
            }
        }

        // If we set a row ID column for this table, it moves all the way to the left
        if (rowIDColumnName !== undefined) {
            const newMoved = new Map(moved);
            const existingMoves = (moved.get(tableName.name) ?? []).filter(([n]) => n !== rowIDColumnName);
            existingMoves.push([rowIDColumnName, 0]);
            newMoved.set(tableName.name, existingMoves);
            moved = newMoved;
        }

        const movedColumns = sortBy(moved.get(tableName.name) ?? [], m => m[1]);
        const reorderedColumns = newColumns.filter(c => !movedColumns.some(m => m[0] === c.name));
        for (const [name, index] of movedColumns) {
            const c = getTableColumn(newColumns, name);
            if (c === undefined) continue;
            reorderedColumns.splice(index, 0, c);
        }

        let result = { ...table, columns: reorderedColumns };
        if (
            rowIDColumnName !== undefined &&
            canBeRowIDColumn(result, rowIDColumnName) &&
            sourceMetadata !== undefined
        ) {
            if (isNative) {
                if (rowIDColumnName !== result.rowIDColumn) {
                    errors.push("Wrong name for native row ID column");
                }
            } else {
                result = { ...result, rowIDColumn: rowIDColumnName };
            }
        }

        return result;
    });

    const schema = makeTypeSchema(tables);
    const newSchema = sanitizeSchemaAfterChanges(schema, userProfileTableInfo);
    // Sanitizing the schema can change primitive types of computed columns when
    // types of basic columns change.  For example, changing a string to a date
    // column will change the type of a lookup column that targets that date
    // column.  We're comparing here to make sure that only primitive types
    // change.
    const schemaDifferences = compareSchemas(schema, newSchema, false, areColumnTypesEqualExceptPrimitives);
    if (schemaDifferences !== undefined) {
        errors.push("Sanitizing the schema changed it");
    }

    const inspector = makeSimpleSchemaInspector(newSchema, userProfileTableInfo);
    if (doesSchemaContainCycles(inspector)) {
        // ##schemasWithCycles:
        // We didn't always correctly detect cycles, and we can't refuse all
        // changes to existing schemas that contain cycles, so we only count
        // it as an error to create a cycle in a schema that didn't already
        // have one.
        const oldInspector = makeSimpleSchemaInspector(oldSchema, userProfileTableInfo);
        if (!doesSchemaContainCycles(oldInspector)) {
            errors.push("Table contains cycle in computed columns");
        }
    }

    return { schema: newSchema, errors };
}

export function compileAndEvaluateComputedColumn(
    schema: TypeSchema,
    userProfileTableInfo: UserProfileTableInfo | undefined,
    table: TableGlideType,
    columnName: string,
    formula: Formula,
    isBuilder: boolean,
    forDataEditor: boolean
): Evaluable | undefined {
    const serializedFormula = compileFormula(
        new SimpleFetchingEvaluatingBuilder(schema, userProfileTableInfo, isBuilder),
        table,
        formula,
        true,
        forDataEditor,
        makeUniqueIDForColumnFormula(getTableName(table), columnName)
    );
    if (!serializedFormula.success) return undefined;
    return deserializeEvaluable(serializedFormula.value);
}
